import { useAtomValue } from "jotai";
import type { PageProps } from "next";
import { useEffect } from "react";
import { graphql, usePreloadedQuery } from "react-relay";
import { withRelay } from "relay-nextjs";
import type { RelayComponent } from "relay-nextjs";

import { data } from "shared/data";
import { parseQueryString } from "shared/lib/utils";

import { useTopBannerAdSlot } from "scmp-app/components/advertisement/ad-slots/top-banner-ad-slot/hooks";
import { TargetingPaidType } from "scmp-app/components/advertisement/ad-slots/types";
import { AuthorAvatarContextProvider } from "scmp-app/components/author/author-avatar/contexts";
import { AuthorContentList } from "scmp-app/components/author/author-content-list";
import { AuthorHeader } from "scmp-app/components/author/author-header";
import { AuthorLinkingData } from "scmp-app/components/author/author-linking-data";
import { AuthorRelatedTopics } from "scmp-app/components/author/author-related-topics";
import { AuthorSeo } from "scmp-app/components/author/author-seo";
import { isSCMPColumnist, isSCMPEditorial } from "scmp-app/components/author/helpers";
import { FallbackLoading } from "scmp-app/components/fallback-loading";
import { trackingAtom } from "scmp-app/components/tracking";
import { createClientEnvironment } from "scmp-app/lib/relay/environment.client";
import type { authorPageQuery } from "scmp-app/queries/__generated__/authorPageQuery.graphql";

import { Bio, Container, List, Right } from "./styles";

const query = graphql`
  query authorPageQuery(
    $entityId: String!
    $applicationIds: [String]
    $after: String
    $count: Int = 30
  ) {
    author(filter: { entityId: $entityId }) {
      entityId
      entityUuid
      name
      urlAlias
      types
      ...authorContentListAuthor
        @arguments(applicationIds: $applicationIds, after: $after, count: $count)
      ...authorRelatedTopicsAuthor
        @arguments(applicationIds: $applicationIds, after: $after, count: $count)
      ...authorLinkingDataAuthor
      ...authorHeaderAuthor
      ...authorSeoAuthor
    }
  }
`;

const AuthorPage: RelayComponent<unknown, authorPageQuery> = ({ preloadedQuery }) => {
  const queryReference = usePreloadedQuery(query, preloadedQuery);

  const { trackPageView } = useAtomValue(trackingAtom);

  useEffect(() => {
    const { entityId, entityUuid, name, urlAlias } = queryReference.author;
    trackPageView?.({
      authors: name,
      contentType: "Author",
      entityId,
      entityUuid,
      page: "Author",
      path: urlAlias,
    });
  }, [trackPageView, queryReference.author]);

  useTopBannerAdSlot({
    desktop: {
      adUnit: "d_banner2b",
      sizes: [
        [970, 250],
        [728, 90],
        [970, 90],
      ],
      targeting: {
        paid: TargetingPaidType.NotArticle,
        sctid: queryReference.author.entityId,
      },
      zone: "comment_author/home",
    },
    mobile: {
      adUnit: "m_banner3",
      targeting: {
        paid: TargetingPaidType.NotArticle,
        sctid: queryReference.author.entityId,
      },
      zone: "comment_author/home",
    },
  });

  return (
    <AuthorAvatarContextProvider
      showBorderColor={
        isSCMPEditorial([queryReference.author.entityId]) ||
        isSCMPColumnist(queryReference.author.types)
      }
    >
      <AuthorSeo reference={queryReference.author} />
      <AuthorLinkingData reference={queryReference.author} />
      <Container>
        <Bio>
          <AuthorHeader reference={queryReference.author} />
        </Bio>
        <List>
          <AuthorContentList reference={queryReference.author} />
        </List>
        <Right>
          <AuthorRelatedTopics reference={queryReference.author} />
        </Right>
      </Container>
    </AuthorAvatarContextProvider>
  );
};

AuthorPage.displayName = "AuthorPage";

const pageProps: PageProps = {
  appBarConfiguration: {
    adSlotVariants: {
      large: {
        height: 250,
        padding: 20,
      },
      small: {
        height: 100,
        padding: 10,
      },
    },
    hasDesktopAd: true,
    hasMobileAd: true,
    variant: "scmp/slim",
  },
};

export default withRelay(AuthorPage, query, {
  clientSideProps: () => pageProps,
  createClientEnvironment: () => createClientEnvironment(),
  createServerEnvironment: async context => {
    const { createServerEnvironment } = await import("scmp-app/lib/relay/environment.server");
    return createServerEnvironment(context.req?.cookies);
  },
  fallback: <FallbackLoading />,
  serverSideProps: () => Promise.resolve(pageProps),
  variablesFromContext(context) {
    return {
      applicationIds: [data.application.scmp.entityUuid],
      entityId: parseQueryString(context.query.entityId) ?? "",
    };
  },
});
